import { useSeoMeta } from '@unhead/react';
import { Link, useParams } from 'react-router-dom';

import { useForumNodes } from '@/hooks/useForum';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  childrenOf,
  formatDate,
  nodeType,
  type ForumNode,
} from '@/lib/forum';
import { ForumHeader } from './ForumHeader';
import { Breadcrumbs } from './Breadcrumbs';
import { ReplyForm } from './ReplyForm';
import { EditNodeDialog } from './EditNodeDialog';
import { ForumFooter } from './ForumFooter';
import { AuthorName, AuthorAvatar } from './Author';
import { PostBody } from './PostBody';
import { Skeleton } from '@/components/ui/skeleton';

export function ThreadView() {
  const { id } = useParams();
  const { data, isLoading } = useForumNodes();
  const { user } = useCurrentUser();

  useSeoMeta({
    title: 'NostrBoard — Topic',
    description: 'A Nostr-powered discussion thread.',
  });

  if (isLoading || !data) return <ThreadPageSkeleton />;

  const thread = id ? data.byId.get(id) : undefined;
  if (!thread || (nodeType(thread, data.byId) !== 'thread' && nodeType(thread, data.byId) !== 'reply')) {
    return <ThreadMissing />;
  }

  const forum = thread.parent ? data.byId.get(thread.parent) : undefined;
  const category = forum?.parent ? data.byId.get(forum.parent) : undefined;

  const replies = childrenOf(data.byId, thread.id).sort((a, b) => a.createdAt - b.createdAt);
  const posts: ForumNode[] = [thread, ...replies];

  return (
    <div className="min-h-screen bg-[#ECECEC]">
      <ForumHeader />
      <main className="mx-auto max-w-[1100px] px-4 py-5">
        <Breadcrumbs
          items={[
            { label: 'Board index', to: '/' },
            ...(category ? [{ label: category.title || 'Category' }] : []),
            ...(forum ? [{ label: forum.title || 'Forum', to: `/f/${forum.id}` }] : []),
            { label: thread.title || 'Topic' },
          ]}
        />

        <h1 className="mb-4 text-2xl font-bold text-[#105B86]">{thread.title || 'Untitled topic'}</h1>

        <div>
          {posts.map((post, i) => (
            <PostCard key={post.id} post={post} index={i} />
          ))}
        </div>

        <ReplyForm threadId={thread.id} />
        {!user && (
          <p className="mt-2 text-center text-xs text-[#999]">
            You must be logged in to reply.
          </p>
        )}

        <ForumFooter />
      </main>
    </div>
  );
}

function PostCard({ post, index }: { post: ForumNode; index: number }) {
  return (
    <div className="mb-3 overflow-hidden rounded-[6px] border border-[#d6d6d6] bg-white">
      <div className="flex items-center justify-between border-b border-[#d6d6d6] bg-[linear-gradient(to_bottom,#f4f4f4,#e4e4e4)] px-3 py-2 text-sm">
        <span className="font-semibold text-[#333]">
          {index === 0 ? 'Topic author' : 'Re:'} <AuthorName pubkey={post.pubkey} className="font-normal" />
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#777]">{formatDate(post.createdAt)}</span>
          <EditNodeDialog
            node={post}
            hasTitle={index === 0}
            label={index === 0 ? 'Edit topic' : 'Edit post'}
          />
        </div>
      </div>
      <div className="flex">
        <div className="hidden w-44 shrink-0 flex-col items-center gap-2 border-r border-[#ececec] bg-[#f8f9fb] p-3 sm:flex">
          <AuthorAvatar pubkey={post.pubkey} size="lg" />
          <AuthorName pubkey={post.pubkey} />
          <div className="text-xs text-[#888]">{post.pubkey.slice(0, 10)}…</div>
        </div>
        <div className="min-w-0 flex-1 p-4">
          <PostBody content={post.content} />
        </div>
      </div>
    </div>
  );
}

function ThreadMissing() {
  return (
    <div className="min-h-screen bg-[#ECECEC]">
      <ForumHeader />
      <main className="mx-auto max-w-[1100px] px-4 py-10">
        <Breadcrumbs items={[{ label: 'Board index', to: '/' }, { label: 'Not found' }]} />
        <div className="rounded-[6px] border border-dashed border-[#c9c9c9] bg-white px-8 py-12 text-center">
          <p className="text-[#666]">This topic could not be found.</p>
          <Link to="/" className="mt-2 inline-block text-[#105B86] hover:underline">
            Return to the board index
          </Link>
        </div>
        <ForumFooter />
      </main>
    </div>
  );
}

function ThreadPageSkeleton() {
  return (
    <div className="min-h-screen bg-[#ECECEC]">
      <ForumHeader />
      <main className="mx-auto max-w-[1100px] px-4 py-5">
        <Skeleton className="mb-4 h-8 w-full max-w-xl rounded-[6px]" />
        {[0, 1, 2].map((r) => (
          <div key={r} className="mb-3 overflow-hidden rounded-[6px] border border-[#d6d6d6] bg-white">
            <Skeleton className="h-9 w-full rounded-none" />
            <div className="flex">
              <Skeleton className="hidden h-32 w-44 sm:block" />
              <div className="flex-1 space-y-2 p-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          </div>
        ))}
      </main>
    </div>
  );
}
